import { TAGThemeProvider } from '@aspendental/shared-components-web';
import { AppContextProvider } from '@aspendental/shared-components-web/lib/context/AppContextProvider.ctx';
import { FacilityContextProvider } from '@aspendental/shared-components-web/lib/context/FacilityContextProvider.ctx';
import { TAGBrand } from '@aspendental/shared-components-web/lib/types';
import { ContentfulLivePreviewProvider } from '@contentful/live-preview/react';
import { StyledEngineProvider } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { Client, Provider as GraphQueryProvider, cacheExchange, fetchExchange } from 'urql';
import { PortalThemeProvider } from './PortalThemeProvider';
import { getCookie } from '@src/pages/utils/get-cookie';
import { useAppStore } from '@src/store';
const queryClient = new QueryClient();
const getGraphClient = (graphURL: string) => {
  if (!graphURL) {
    return null;
  }
  return new Client({
    url: graphURL,
    exchanges: [cacheExchange, fetchExchange],
    requestPolicy: 'network-only',
    fetchOptions: () => {
      return {
        credentials: 'include',
        headers: {
          'x-client-application': process.env.APP_NAME,
          'GraphQL-Preflight': '1',
          'X-Source': getCookie('isOneClickLogin') ? 'PortalCRM1Click' : 'Portal'
        }
      };
    }
  });
};
interface IContextProvidersProps {
  config: any;
  preview: boolean;
  environment: 'local' | 'dev' | 'stage' | 'prod';
  graphURL: string;
  googleURL: string;
  user: any;
  children: React.ReactNode;
  resultURL: string;
  googleMapApiKey: string;
  eapiBaseUrl: string;
  userEventsURL: string;
  formEventsURL: string;
  previsitPaperworkUrl?: string;
  appData: any;
  stateCodeFromHeaders?: string;
}
export const ContextProviders = ({
  config,
  preview,
  environment,
  graphURL,
  googleURL,
  user,
  children,
  resultURL,
  googleMapApiKey,
  eapiBaseUrl,
  userEventsURL,
  formEventsURL,
  previsitPaperworkUrl = '',
  appData,
  stateCodeFromHeaders
}: IContextProvidersProps) => {
  const router = useRouter();
  const [isClient, setIsClient] = useState(false);
  const brandTheme = config?.themeId || process.env.TAG_BRAND_THEME || 'aspendental';
  const brandName = brandTheme as TAGBrand;
  const graphClient = getGraphClient(graphURL);
  useEffect(() => {
    setIsClient(true);
  }, []);
  const setRestUrl = useAppStore(store => store.setRestUrl);
  const setGraphUrl = useAppStore(store => store.setGraphUrl);
  const setGoogleUrl = useAppStore(store => store.setGoogleUrl);
  const setUserEventsUrl = useAppStore(store => store.setUserEventsUrl);
  const setFormEventsUrl = useAppStore(store => store.setFormEventsUrl);
  const setGoogleMapApiKey = useAppStore(store => store.setGoogleMapApiKey);
  const setEapiBaseUrl = useAppStore(store => store.setEapiBaseUrl);
  const setPrevisitPaperworkUrl = useAppStore(store => store.setPrevisitPaperworkUrl);
  const setIsPatientPortalPhase2Enabled = useAppStore(store => store.setIsPatientPortalPhase2Enabled);
  const setIsPatientPortalNavigationEnabled = useAppStore(store => store.setIsPatientPortalNavigationEnabled);
  const setUserStateCodeForAnalytics = useAppStore(store => store.setUserStateCodeForAnalytics);
  const setOfficeRollout = useAppStore(store => store.setOfficeRollout);
  const setEnableFraudCheck = useAppStore(store => store.setEnableFraudCheck);
  useEffect(() => {
    setRestUrl(resultURL);
    setGraphUrl(graphURL);
    setGoogleUrl(googleURL);
    setUserEventsUrl(userEventsURL);
    setFormEventsUrl(formEventsURL);
    setGoogleMapApiKey(googleMapApiKey);
    setEapiBaseUrl(eapiBaseUrl);
    setPrevisitPaperworkUrl(previsitPaperworkUrl);
    setUserStateCodeForAnalytics(stateCodeFromHeaders || '');
  }, [eapiBaseUrl, googleMapApiKey, googleURL, graphURL, resultURL, userEventsURL, formEventsURL, setFormEventsUrl, previsitPaperworkUrl, setEapiBaseUrl, setGoogleMapApiKey, setGoogleUrl, setGraphUrl, setRestUrl, setUserEventsUrl, setPrevisitPaperworkUrl]);
  useEffect(() => {
    setIsPatientPortalPhase2Enabled(appData?.featureFlags?.enablePatientPortalPhase2);
    setIsPatientPortalNavigationEnabled(appData?.featureFlags?.enablePatientPortalNavigation);
    setOfficeRollout(appData?.featureFlags?.officeRollout);
    setEnableFraudCheck(appData?.featureFlags?.enableFraudCheck);
  }, [appData?.featureFlags?.enablePatientPortalPhase2, appData?.featureFlags?.enablePatientPortalNavigation, appData?.featureFlags?.officeRollout, appData?.featureFlags?.enableFraudCheck, setIsPatientPortalPhase2Enabled, setIsPatientPortalNavigationEnabled, setOfficeRollout, setEnableFraudCheck]);
  const content = <QueryClientProvider client={queryClient}>
			<ContentfulLivePreviewProvider locale={router.locale || 'en-US'} enableInspectorMode={preview} enableLiveUpdates={preview}>
				<AppContextProvider config={config} theme={brandName} environment={environment} appName={process.env.APP_NAME || 'tag_dev'} appVersion={`${process.env.APP_VERSION || 'unknown'}(design-system:${process.env.DESIGN_SYSTEM_VERSION})`} stateCodeFromHeaders={stateCodeFromHeaders}>
					<StyledEngineProvider injectFirst>
						<TAGThemeProvider branding={brandName as TAGBrand}>
							<PortalThemeProvider>
								{isClient && <FacilityContextProvider appName="tag_dev" appVersion="0.0.0" brand={brandName as TAGBrand} user={user}>
										{children}
									</FacilityContextProvider>}
							</PortalThemeProvider>
						</TAGThemeProvider>
					</StyledEngineProvider>
				</AppContextProvider>
			</ContentfulLivePreviewProvider>
			<ReactQueryDevtools initialIsOpen={false} />
		</QueryClientProvider>;
  return graphClient ? <GraphQueryProvider value={graphClient} data-sentry-element="GraphQueryProvider" data-sentry-component="ContextProviders" data-sentry-source-file="ContextProviders.tsx">{content}</GraphQueryProvider> : content;
};